// ProductItem.tsx
import {
  ProductInterface,
} from "../../pages/OrderDetailsPage/OrderDetailsInterface";
import { useAuth } from "../../context/AuthContext";
import { formatCurrency, getCurrencySymbol } from "../../utils/formatters";

interface ProductItemProps {
  product: ProductInterface;
  detailId?: string;
}

export const ProductItem = ({ product }: ProductItemProps) => {
  const { country } = useAuth();
  const currencySymbol = getCurrencySymbol(country);

  const variants = product.product_variants ?? [];
  const unitPrice = product.unit_price_with_discount;
  // Detect per-variant custom pricing: at least one variant has a price
  // that differs from the product's unit price. In that case each variant
  // shows its own price and the misleading `unitPrice × units` summary
  // line is hidden.
  const hasVariantPricing = variants.some(
    (v) => Number(v.price) > 0 && Number(v.price) !== unitPrice
  );

  return (
    <div className="flex items-start gap-4 py-4 border bg-white border-gray-200 px-4 rounded-lg m-4">
      {/* Product Image */}
      <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
        {product.images && product.images.length > 0 ? (
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-400 text-xs">No image</span>
          </div>
        )}
      </div>

      {/* Product Details */}
      <div className="flex-grow">
        <h3 className="font-medium text-gray-900 text-base">{product.name}</h3>

        {/* Variant breakdown per unit */}
        {variants.length > 0 && (
          <div className="mt-1 space-y-1">
            {variants.map((variant) => {
              const attributeText = variant.attributes.map((attribute) => {
                const isColorVariant = 'color' in attribute;
                const displayValue = isColorVariant
                  ? attribute.color.name
                  : attribute.value;
                return `${attribute.name}: ${displayValue}`;
              }).join(', ');

              const variantPrice =
                Number(variant.price) > 0 ? Number(variant.price) : unitPrice;

              return (
                <div key={variant.id} className="flex items-center gap-2 flex-wrap">
                  <span className="text-gray-400 text-xs">↳</span>
                  <span className="inline-block bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full">
                    {attributeText}
                  </span>
                  {variant.units > 1 && (
                    <span className="text-xs text-purple-600 font-medium">
                      ×{variant.units}
                    </span>
                  )}
                  {hasVariantPricing && (
                    <span className="text-xs text-gray-700 font-medium">
                      {formatCurrency(variantPrice, currencySymbol)}
                      {variant.units > 1 && (
                        <span className="text-gray-400 font-normal">
                          {" "}c/u
                        </span>
                      )}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* SKU Line */}
        <p className="mt-1 text-xs text-gray-500">SKU: {product.product_id}</p>

        {/* Price line + total */}
        <div className="text-right flex flex-row justify-between">
          {!hasVariantPricing ? (
            <p className="mt-1 text-sm text-gray-600">
              {formatCurrency(unitPrice, currencySymbol)} × {product.units || 1}
            </p>
          ) : (
            <span />
          )}
          <p className="font-medium text-gray-900">
            {formatCurrency(product.total_value_with_discount, currencySymbol)}
          </p>
        </div>
      </div>
    </div>
  );
};
